define('viewModels/privileged/qm/AcdVoicemailView',[
    'lib/knockout',
    'lib/lodash4',
    'lib/bluebird',
    'moment',
    'services/authService',
    'services/loggingService',
    'services/voicemailService',
    'util/errorCallbackFactory',
    'viewModels/widgets/audio/AudioPlayerView',
    'viewModels/ViewModel'
], function (ko,
             _,
             Promise,
             moment,
             authService,
             LOG,
             voicemailService,
             errorCallbackFactory,
             AudioPlayerView,
             ViewModel
) {
    'use strict';

    var AcdVoicemailView = ViewModel.extend(function(voicemailId, options){
        var self = this;

        var audioOptions = _.assign({
            autoplay: false,
            darkMode: true,
            showAnnotations: false
        }, _.omit(options, 'displayed'));

        self.displayed = ko.observable(options ? !!options.displayed : false);

        self.voicemail = ko.observable();
        self.isVoicemail = true;

        // <!-- Need to implement some otherwise unused properties for RecordingView interface
            var readOnlyObservableFalse = self.computed(function () {
                return false;
            });
            self.audioPlayerView = self.subView(AudioPlayerView, null, voicemailId, audioOptions);
            self.hasAnnotationText = readOnlyObservableFalse;
            self.hasChat = readOnlyObservableFalse;
            self.hasMessages = readOnlyObservableFalse;
            self.hasScreenRecording = readOnlyObservableFalse;
            self.isRecordingAvailable = readOnlyObservableFalse;
            self.keywordSpots = ko.observableArray();
            self.isAvailableAudioRecordingWithMissingMedia = readOnlyObservableFalse;
        // --> end of RecordingView vars

        self.isAcdVoicemail = true;
        self.isWorking = ko.observable(false);
        self.showDeleteConfirmation = ko.observable(false);
        self.deleteVoicemail = function () {
            self.isWorking(true);
            self.showDeleteConfirmation(false);
            return voicemailService.deleteVoicemail(voicemailId)
                .then(function () {
                    self.voicemail(null);
                    self.hasAudio(false);
                    self.isDeleted(true);
                })
                .catch(errorCallbackFactory.createCallback({ namespace: "playback.acdVoicemailDelete"}))
                .finally(function () {
                    self.isWorking(false);
                });
        };
        self.keep = function () {
            self.isWorking(true);
            var policy = { voicemailRetentionPolicyType: 'RETAIN_INDEFINITELY' };
            return voicemailService.updateVoicemailRetention(voicemailId, policy)
                .then(function (voicemailResponse) {
                    voicemailMetadata(voicemailResponse);
                })
                .catch(errorCallbackFactory.createCallback({ namespace: "playback.acdVoicemailKeep"}))
                .finally(function () {
                    self.isWorking(false);
                });
        };
        self.editingDownload = ko.observable(false);
        self.editDownload = function(){
            self.editingDownload(true);
        };
        self.cancelDownloadEdit = function(){
            self.editingDownload(false);
        };
        self.fileFormatOptions = ko.observable([
            {text: ko.i18n.renderString('conversationDetails.mp3'), value: "mp3"},
            {text: ko.i18n.renderString('conversationDetails.wav'), value: "wav"},
            {text: ko.i18n.renderString('conversationDetails.webm'), value: "webm"}
        ]);
        self.selectedFileFormat = ko.observable();
        self.downloading = ko.observable(false);
        self.download = function () {
            self.downloading(true);
            getVoicemailMedia(voicemailId, {'formatId': self.selectedFileFormat()})
            .then(function (voicemail) {
                var link = document.getElementsByClassName("conversation-download-voicemail");
                link[0].href = ko.unwrap(voicemail).mediaFileUri;
                link[0].click();
            })
            .finally(function () {
                self.downloading(false);
            });
        };
        self.confirmDelete = function () {
            self.showDeleteConfirmation(true);
        };

        self.hasAudio = ko.observable(false);
        self.isRecordingLoading = ko.observable(true);
        self.showLongRunningMessage = ko.observable(false);
        self.recordingRequestError = ko.observable(false);
        self.isDeleted = ko.observable(false);
        self.canDelete = authService.hasAnyAuthorization(['perm:voicemail:acdVoicemail:delete']);
        self.recordingDeletedTitle = 'playback.voicemailIsDeleted.title';
        self.recordingDeletedDescription = 'playback.voicemailIsDeleted.description';

        var voicemailMetadata = ko.observable();
        self.deleteDate = self.computed(function () {
            var calculatedDeleteDate = null;
            var vmMetadata = ko.unwrap(voicemailMetadata);
            if (vmMetadata && vmMetadata.deleteRetentionPolicy) {
                var deleteRetentionPolicyType = vmMetadata.deleteRetentionPolicy.voicemailRetentionPolicyType;
                var ttl_days = vmMetadata.deleteRetentionPolicy.numberOfDays || 0;
                if (deleteRetentionPolicyType === "RETAIN_WITH_TTL" && vmMetadata.deletedDate) {
                    calculatedDeleteDate = moment(vmMetadata.deletedDate).add(ttl_days, 'days').toDate();
                } else if (deleteRetentionPolicyType === "RETAIN_WITH_TTL" || deleteRetentionPolicyType === "IMMEDIATE_DELETE") {
                    calculatedDeleteDate = moment(vmMetadata.createdDate).add(60 + ttl_days, 'days').toDate();
                }
                // else if policy is KEEP_INDEFINITELY, calculatedDeleteDate = null
            }
            return calculatedDeleteDate;
        });

        var POLLING_INTERVAL = options && options.pollingInterval || 3000;
        var MAX_RETRIES = options && options.maxRetries || 10;

        _.assign(self, audioOptions);

        var handleError = function(errorResponse) {
            if (errorResponse &&
                    errorResponse.details &&
                    errorResponse.details.status === 404) {
                self.isDeleted(true);
            }
            LOG.info("Error loading acd voicemail", errorResponse);
        };

        // Get voicemail metadata
        voicemailService.getVoicemail(voicemailId)
            .then(function (voicemailResponse) {
                voicemailMetadata(voicemailResponse);
            })
            .catch(handleError);

        // Get voicemail media
        var getVoicemailMedia = function (voicemailId, options) {
            var formatId = options && options.formatId;

            // Poll while response is 202. Need a 200.
            return new Promise(function (resolve, reject) {
                var _getVoicemailMedia = function(voicemailId, retryCount) {
                    voicemailService.getVoicemailMedia(voicemailId, formatId)
                        .then(function (voicemailResponse) {
                            if (voicemailResponse && voicemailResponse.mediaFileUri) {
                                resolve(voicemailResponse);
                            } else if (retryCount > 0) {
                                self.showLongRunningMessage(true);
                                setTimeout(function () {
                                    _getVoicemailMedia(voicemailId, retryCount - 1);
                                }, POLLING_INTERVAL);
                            } else {
                                LOG.info("Reached maximum retries for loading acdVoicemail", { maxRetries: MAX_RETRIES });
                                reject({});
                            }
                        })
                        .catch(handleError);
                };
                _getVoicemailMedia(voicemailId, MAX_RETRIES - 1);
            });
        };

        getVoicemailMedia(voicemailId, {'formatId': 'mp3'})
            .then(function (voicemail) {
                self.voicemail(voicemail);
            }).catch(function (error) {
                errorCallbackFactory.createCallback({ namespace: "playback.acdVoicemailRequest" })(error);
                self.recordingRequestError(true);
            })
            .finally(function () {
                self.isRecordingLoading(false);
            });

        var isAudioElementRendered = ko.observable(false);
        self.audioRendered = function () {
            isAudioElementRendered(true);
        };

        self.computed(function () {
            if (self.voicemail()) {
                self.hasAudio(!!self.voicemail().mediaFileUri);
                if (isAudioElementRendered()) {
                    var unwrapped = ko.toJS(self.voicemail);
                    self.audioPlayerView.setChannels([{
                        audioUri: unwrapped.mediaFileUri,
                        waveformData: unwrapped.waveformData,
                        waveformUri: unwrapped.waveUri
                    }]);
                }
            }
        });

        self.pause = function() {
            if (self.audioPlayerView.isPlaying()){
                self.audioPlayerView.pause();
            }
        };

        self.play = function() {
            if (self.audioPlayerView.isPlaying()){
                self.audioPlayerView.play();
            }
        };
    });

    return AcdVoicemailView;
});

